/**
 * Qué mes se está MIRANDO en la conciliación.
 *
 * No decide adónde va un ticket —eso lo dice la fecha del propio ticket—, solo qué mes enseña la
 * tabla. Botones y no un desplegable: los años arriba y los doce meses a la vista, a un clic, y
 * los que se quedaron sin cuadrar con un punto rojo, que se ven sin tener que abrir nada.
 *
 * «Todo», delante de los meses, enseña el año entero en una sola tabla: la vista general.
 */
import { MONTH_LABELS_ES, MONTH_NAMES_ES } from '@naar/shared';
import styles from './MonthPicker.module.css';

interface MonthPickerProps {
  month: string;
  years: number[];
  pendingMonths: { month: string; gapCount: number }[];
  /** Se eligió un mes concreto. */
  onChange: (month: string) => void;
  /** Se pulsó un año: la página enseña ese año entero (como «Todo»). */
  onYearChange?: (month: string) => void;
  /** «Todo» está activo: el año entero. */
  all?: boolean;
  onAll?: () => void;
}

export function MonthPicker({ month, years, pendingMonths, onChange, onYearChange, all = false, onAll }: MonthPickerProps) {
  const year = Number(month.slice(0, 4));
  const anios = [...new Set([...years, year])].sort((a, b) => a - b);

  return (
    <div className={styles.picker}>
      {/* El año siempre a la vista, aunque solo haya uno: se ve de qué año son los meses. */}
      {(
        <div className={styles.years} role="group" aria-label="Año">
          {anios.map((candidate) => (
            <button
              key={candidate}
              type="button"
              className={candidate === year ? `${styles.year} ${styles.yearActive}` : styles.year}
              onClick={() => (onYearChange ?? onChange)(`${candidate}-${month.slice(5, 7)}`)}
              aria-pressed={candidate === year}
            >
              {candidate}
              {/* Un año con algún mes sin cuadrar también lo dice, para no tener que ir a buscarlo. */}
              {candidate !== year && pendingMonths.some((row) => row.month.startsWith(`${candidate}-`)) && (
                <span className={styles.dot} aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      )}

      <div className={styles.months} role="group" aria-label={`Mes de ${year}`}>
        {onAll && (
          <button
            type="button"
            className={all ? `${styles.month} ${styles.all} ${styles.monthActive}` : `${styles.month} ${styles.all}`}
            onClick={onAll}
            aria-pressed={all}
            aria-label={`Todo ${year}`}
            title={`Todos los movimientos de ${year}`}
          >
            Todo
          </button>
        )}
        {MONTH_LABELS_ES.map((label, index) => {
          const key = `${year}-${String(index + 1).padStart(2, '0')}`;
          const pending = pendingMonths.find((row) => row.month === key);
          const active = !all && key === month;
          const nombre = MONTH_NAMES_ES[index] ?? label;
          return (
            <button
              key={key}
              type="button"
              className={[styles.month, active ? styles.monthActive : '', pending ? styles.monthPending : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => onChange(key)}
              aria-pressed={active}
              aria-label={pending ? `${nombre}: ${pending.gapCount} sin cuadrar` : nombre}
              title={pending ? `${nombre}: ${pending.gapCount} sin cuadrar` : nombre}
            >
              {label}
              {pending && <span className={styles.dot} aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
